import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Pencil, Download, RotateCw, Package, MapPin, Heart,
  ShoppingBag, ShieldCheck, Star, CircleCheck, X, AlertTriangle, Gift, LifeBuoy, Send, MessageCircle,
} from "lucide-react";
import { api, API, fmtPrice } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

const inputCls = "w-full bg-[#171717] border border-white/15 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#4ADE80] focus:border-transparent transition-[box-shadow] placeholder:text-[#7a7a7a]";
const btnPrimary = "btn-press bg-[#4ADE80] text-[#0F0F10] font-medium px-6 py-3 hover:bg-[#6FE998] hover:shadow-lg hover:shadow-[#4ADE80]/20 transition-[background-color,box-shadow] disabled:opacity-50 flex items-center justify-center gap-2";
const btnGhost = "btn-press border border-white/10 rounded-full text-[13px] font-medium px-5 py-3 hover:border-white/25 hover:bg-[#0F0F10] transition-[border-color,background-color]";

const Head = ({ eyebrow, title, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
    <div>
      <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-3">{eyebrow}</p>
      <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-none">{title}</h2>
    </div>
    {children}
  </div>
);

const Loading = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ADE80]" /></div>;

/* ---------------- Overview ---------------- */
export function Overview({ user, setTab }) {
  const [orders, setOrders] = useState(null);
  const [addr, setAddr] = useState(0);
  const [wish, setWish] = useState(0);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    api.get("/account/addresses").then(({ data }) => setAddr(data.length)).catch(() => {});
    api.get("/account/wishlist").then(({ data }) => setWish(data.length)).catch(() => {});
    api.get("/account/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);

  const completionFields = ["name", "phone", "company", "currency", "language"];
  const completion = profile ? Math.round((completionFields.filter((f) => profile[f]).length / completionFields.length) * 100) : null;

  const cards = [
    { label: "Recent Orders", value: orders ? orders.length : "—", icon: Package, tab: "orders" },
    { label: "Saved Addresses", value: addr, icon: MapPin, tab: "addresses" },
    { label: "Wishlist Items", value: wish, icon: Heart, tab: "wishlist" },
  ];

  return (
    <div data-testid="account-overview">
      <Head eyebrow="Dashboard" title={`Welcome, ${user.name?.split(" ")[0] || "Researcher"}`} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <button key={c.label} onClick={() => setTab(c.tab)} className="border border-white/10 rounded-2xl p-6 text-left hover:border-white/25 transition-colors">
            <c.icon className="h-6 w-6 text-[#4ADE80]" strokeWidth={1.5} />
            <p className="mt-6 font-display font-black text-4xl tracking-tighter">{c.value}</p>
            <p className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] mt-1">{c.label}</p>
          </button>
        ))}
      </div>

      <div className="mt-6 border border-white/10 rounded-2xl p-6">
        <div className="flex justify-between items-center">
          <p className="font-display font-bold uppercase tracking-tight">Account Completion</p>
          <span className="font-mono-accent text-sm text-[#4ADE80]">{completion === null ? "…" : `${completion}%`}</span>
        </div>
        <div className="mt-4 h-2 bg-[#1F1F1F] rounded-full overflow-hidden"><div className="h-full bg-[#4ADE80] transition-all" style={{ width: `${completion ?? 0}%` }} /></div>
        {completion !== null && completion < 100 && <button onClick={() => setTab("profile")} className="mt-4 font-mono-accent text-xs text-[#A8A8A8] hover:text-[#4ADE80] link-underline">Complete your profile →</button>}
      </div>

      <div className="mt-6 border border-white/10 rounded-2xl p-6">
        <div className="flex justify-between items-center mb-4">
          <p className="font-display font-bold uppercase tracking-tight">Recent Orders</p>
          <button onClick={() => setTab("orders")} className="font-mono-accent text-[11px] uppercase tracking-widest text-[#A8A8A8] hover:text-[#4ADE80]">View all</button>
        </div>
        {orders === null ? <Loading /> : orders.length === 0 ? (
          <p className="font-mono-accent text-sm text-[#7a7a7a] py-6 text-center">No orders yet — <Link to="/products" className="text-[#4ADE80] link-underline">browse the catalog</Link></p>
        ) : (
          <div className="divide-y divide-white/10">
            {orders.slice(0, 3).map((o) => (
              <div key={o.session_id} className="flex justify-between items-center py-3 font-mono-accent text-sm">
                <span className="text-[#E5E5E5] truncate">{(o.items || []).join(", ")}</span>
                <span className="text-[#4ADE80] whitespace-nowrap ml-3">{fmtPrice(o.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Profile ---------------- */
export function ProfileSection() {
  const { checkAuth } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/account/profile").then(({ data }) => {
      setForm(data);
      if (data.avatar_path) {
        // Pull the avatar as a blob so we can render without exposing the storage URL
        api.get("/account/avatar", { responseType: "blob" })
          .then((res) => setAvatarUrl(URL.createObjectURL(res.data)))
          .catch(() => {});
      }
    }).catch(() => {});
  }, []);
  useEffect(() => () => { if (avatarUrl) URL.revokeObjectURL(avatarUrl); }, [avatarUrl]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB"); return; }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/account/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, avatar_path: data.avatar_path }));
      const res = await api.get("/account/avatar", { responseType: "blob" });
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
      setAvatarUrl(URL.createObjectURL(res.data));
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setAvatarBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    try {
      await api.delete("/account/avatar");
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
      setAvatarUrl(null);
      setForm((prev) => ({ ...prev, avatar_path: null }));
      toast.success("Avatar removed");
    } catch { toast.error("Failed to remove avatar"); }
  };

  if (!form) return <Loading />;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, phone: form.phone, company: form.company, tax_number: form.tax_number,
        language: form.language, currency: form.currency, dob: form.dob,
        comms_opt_in: !!form.comms_opt_in, newsletter_opt_in: !!form.newsletter_opt_in,
      };
      const { data } = await api.put("/account/profile", payload);
      setForm({ ...data, avatar_path: form.avatar_path });
      checkAuth();
      toast.success("Profile saved.");
    } catch { toast.error("Could not save profile."); } finally { setSaving(false); }
  };

  return (
    <div data-testid="account-profile">
      <Head eyebrow="Profile" title="Your Details" />

      {/* Avatar uploader */}
      <div className="flex items-center gap-5 mb-8">
        <div className="h-24 w-24 rounded-full border border-white/15 bg-[#171717] overflow-hidden flex items-center justify-center">
          {avatarUrl
            ? <img data-testid="profile-avatar-image" src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
            : <div className="text-3xl font-display text-[#A8A8A8]">{(form.name || form.email || "?").slice(0, 1).toUpperCase()}</div>}
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="avatar-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={avatarBusy}
              className="btn-press border border-white/20 text-[#F5F5F5] px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] hover:border-[#4ADE80] hover:text-[#4ADE80] disabled:opacity-50"
            >
              {avatarBusy ? "Uploading…" : (avatarUrl ? "Change photo" : "Upload photo")}
            </button>
            {avatarUrl && (
              <button
                type="button"
                data-testid="avatar-remove-btn"
                onClick={removeAvatar}
                className="btn-press border border-white/20 text-[#A8A8A8] px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] hover:border-red-400/40 hover:text-red-400"
              >
                Remove
              </button>
            )}
          </div>
          <div className="text-[10px] text-[#7a7a7a]">JPG, PNG or WebP · max 2MB</div>
          <input
            data-testid="avatar-file-input"
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onFile}
            className="hidden"
          />
        </div>
      </div>

      <form onSubmit={save} className="space-y-4 max-w-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Full Name</label><input data-testid="profile-name" className={inputCls} value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Email</label><input className={inputCls + " opacity-60"} value={form.email || ""} disabled /></div>
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Phone</label><input data-testid="profile-phone" className={inputCls} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Company</label><input data-testid="profile-company" className={inputCls} value={form.company || ""} onChange={(e) => set("company", e.target.value)} /></div>
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Tax Number</label><input className={inputCls} value={form.tax_number || ""} onChange={(e) => set("tax_number", e.target.value)} /></div>
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Date of Birth</label><input type="date" className={inputCls} value={form.dob || ""} onChange={(e) => set("dob", e.target.value)} /></div>
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Preferred Language</label>
            <select className={inputCls} value={form.language || "English"} onChange={(e) => set("language", e.target.value)}><option>English</option><option>Français</option><option>Español</option><option>Deutsch</option></select></div>
          <div><label className="font-mono-accent text-[11px] uppercase tracking-widest text-[#7a7a7a] block mb-2">Preferred Currency</label>
            <select data-testid="profile-currency" className={inputCls} value={form.currency || "USD"} onChange={(e) => set("currency", e.target.value)}><option>USD</option><option>CAD</option><option>EUR</option><option>GBP</option></select></div>
        </div>
        <label className="flex items-center gap-3 font-mono-accent text-sm text-[#E5E5E5]"><input type="checkbox" checked={!!form.comms_opt_in} onChange={(e) => set("comms_opt_in", e.target.checked)} className="accent-[#4ADE80] h-4 w-4" /> Receive account & order communications</label>
        <label className="flex items-center gap-3 font-mono-accent text-sm text-[#E5E5E5]"><input type="checkbox" checked={!!form.newsletter_opt_in} onChange={(e) => set("newsletter_opt_in", e.target.checked)} className="accent-[#4ADE80] h-4 w-4" /> Subscribe to the research bulletin</label>
        <button data-testid="profile-save" type="submit" disabled={saving} className={btnPrimary}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes</button>
      </form>
    </div>
  );
}

/* ---------------- Addresses ---------------- */
const emptyAddress = { label: "Shipping", full_name: "", company: "", street: "", apartment: "", city: "", province: "", postal_code: "", country: "Canada", phone: "", instructions: "", is_default_shipping: false, is_default_billing: false };
const LABELS = ["Home", "Work", "Shipping", "Billing", "Research Facility", "Other"];

export function AddressesSection() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // address or emptyAddress
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // address pending deletion
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/account/addresses").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/account/addresses/${editing.id}`, editing);
      else await api.post("/account/addresses", editing);
      setEditing(null);
      await load();
      toast.success("Address saved.");
    } catch { toast.error("Could not save address."); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    setDeleting(true);
    try { await api.delete(`/account/addresses/${id}`); await load(); toast.success("Address removed."); }
    catch { toast.error("Could not remove address."); }
    finally { setDeleting(false); setConfirmDel(null); }
  };

  return (
    <div data-testid="account-addresses">
      <Head eyebrow="Address Book" title="Addresses">
        <button data-testid="add-address-btn" onClick={() => setEditing({ ...emptyAddress })} className={btnGhost}><Plus className="h-4 w-4 inline mr-1" /> Add Address</button>
      </Head>
      {list === null ? <Loading /> : list.length === 0 ? (
        <div className="border border-white/10 rounded-2xl p-12 text-center font-mono-accent text-sm text-[#7a7a7a] uppercase tracking-widest">No addresses yet</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {list.map((a) => (
            <div key={a.id} data-testid={`address-${a.id}`} className="border border-white/10 rounded-2xl p-5">
              <div className="flex justify-between items-start">
                <span className="font-mono-accent text-[10px] uppercase tracking-widest bg-[#4ADE80]/10 text-[#4ADE80] px-2 py-1 rounded-full">{a.label}</span>
                <div className="flex gap-3">
                  <button onClick={() => setEditing(a)} aria-label="Edit"><Pencil className="h-4 w-4 text-[#7a7a7a] hover:text-[#4ADE80]" /></button>
                  <button data-testid={`delete-address-${a.id}`} onClick={() => setConfirmDel(a)} aria-label="Delete"><Trash2 className="h-4 w-4 text-[#7a7a7a] hover:text-[#EF4444]" /></button>
                </div>
              </div>
              <p className="mt-3 font-display font-bold uppercase tracking-tight">{a.full_name}</p>
              <p className="mt-1 font-mono-accent text-sm text-[#A8A8A8] leading-relaxed">{a.street}{a.apartment ? `, ${a.apartment}` : ""}<br />{a.city}, {a.province} {a.postal_code}<br />{a.country}</p>
              <div className="mt-3 flex gap-2 flex-wrap">
                {a.is_default_shipping && <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#7a7a7a] border border-white/10 px-2 py-0.5 rounded-full">Default Shipping</span>}
                {a.is_default_billing && <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#7a7a7a] border border-white/10 px-2 py-0.5 rounded-full">Default Billing</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="address-dialog" className="bg-[#171717] border border-white/10 text-[#F5F5F5] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-black text-2xl uppercase tracking-tighter">{editing?.id ? "Edit Address" : "New Address"}</DialogTitle><DialogDescription className="font-mono-accent text-xs text-[#7a7a7a]">All fields help ensure accurate delivery.</DialogDescription></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-3 mt-2">
              <select className={inputCls} value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })}>{LABELS.map((l) => <option key={l}>{l}</option>)}</select>
              <input data-testid="address-fullname" required placeholder="Full name" className={inputCls} value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} />
              <input placeholder="Company (optional)" className={inputCls} value={editing.company} onChange={(e) => setEditing({ ...editing, company: e.target.value })} />
              <input required placeholder="Street address" className={inputCls} value={editing.street} onChange={(e) => setEditing({ ...editing, street: e.target.value })} />
              <input placeholder="Apartment / Suite (optional)" className={inputCls} value={editing.apartment} onChange={(e) => setEditing({ ...editing, apartment: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="City" className={inputCls} value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
                <input required placeholder="Province / State" className={inputCls} value={editing.province} onChange={(e) => setEditing({ ...editing, province: e.target.value })} />
                <input required placeholder="Postal / ZIP" className={inputCls} value={editing.postal_code} onChange={(e) => setEditing({ ...editing, postal_code: e.target.value })} />
                <input required placeholder="Country" className={inputCls} value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value })} />
              </div>
              <input placeholder="Phone (optional)" className={inputCls} value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              <input placeholder="Delivery instructions (optional)" className={inputCls} value={editing.instructions} onChange={(e) => setEditing({ ...editing, instructions: e.target.value })} />
              <label className="flex items-center gap-3 font-mono-accent text-sm text-[#E5E5E5]"><input type="checkbox" checked={editing.is_default_shipping} onChange={(e) => setEditing({ ...editing, is_default_shipping: e.target.checked })} className="accent-[#4ADE80] h-4 w-4" /> Default shipping address</label>
              <label className="flex items-center gap-3 font-mono-accent text-sm text-[#E5E5E5]"><input type="checkbox" checked={editing.is_default_billing} onChange={(e) => setEditing({ ...editing, is_default_billing: e.target.checked })} className="accent-[#4ADE80] h-4 w-4" /> Default billing address</label>
              <button data-testid="save-address-btn" type="submit" disabled={saving} className={btnPrimary + " w-full"}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Address</button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="delete-address-dialog" className="bg-[#171717] border border-white/10 text-[#F5F5F5] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-2xl uppercase tracking-tighter">Delete address?</DialogTitle><DialogDescription className="font-mono-accent text-xs text-[#7a7a7a]">{confirmDel ? `${confirmDel.full_name || confirmDel.label} — ${confirmDel.street}, ${confirmDel.city}. This cannot be undone.` : ""}</DialogDescription></DialogHeader>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setConfirmDel(null)} className={btnGhost + " flex-1"}><X className="h-4 w-4 inline mr-1" /> Cancel</button>
            <button data-testid="confirm-delete-address-btn" onClick={() => remove(confirmDel.id)} disabled={deleting} className="flex-1 bg-[#EF4444] text-white font-display font-bold uppercase tracking-tight px-6 py-3 hover:bg-[#dc2626] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">{deleting && <Loader2 className="h-4 w-4 animate-spin" />} Delete</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Wishlist ---------------- */
export function WishlistSection() {
  const [items, setItems] = useState(null);
  const { addItem } = useCart();
  const load = () => api.get("/account/wishlist").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (slug) => { await api.delete(`/account/wishlist/${slug}`); load(); };
  const move = (p) => { addItem(p, 1); };

  return (
    <div data-testid="account-wishlist">
      <Head eyebrow="Wishlist" title="Saved" />
      {items === null ? <Loading /> : items.length === 0 ? (
        <div className="border border-white/10 rounded-2xl p-12 text-center">
          <Heart className="h-8 w-8 text-[#7a7a7a] mx-auto" />
          <p className="mt-4 font-mono-accent text-sm text-[#7a7a7a] uppercase tracking-widest">No saved products</p>
          <Link to="/products" className="mt-6 inline-block bg-[#4ADE80] text-[#F5F5F5] font-display font-bold uppercase tracking-tight px-6 py-3 hover:bg-[#0F0F10] transition-colors">Browse Catalog</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.slug} data-testid={`wishlist-${p.slug}`} className="border border-white/10 rounded-2xl p-4 flex gap-4">
              <Link to={`/products/${p.slug}`}><img src={p.image} alt={p.name} className="h-20 w-20 object-cover rounded-xl" /></Link>
              <div className="flex-1 min-w-0">
                <Link to={`/products/${p.slug}`}><h3 className="font-display font-bold uppercase tracking-tight truncate hover:text-[#4ADE80] transition-colors">{p.name}</h3></Link>
                <p className="font-mono-accent text-[#4ADE80] text-sm mt-1">{fmtPrice(p.price)}</p>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`wishlist-move-${p.slug}`} onClick={() => move(p)} className="flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest border border-white/10 px-3 py-2 hover:bg-[#6FE998] hover:text-[#F5F5F5] hover:border-[#4ADE80] transition-colors"><ShoppingBag className="h-3 w-3" /> Add to Cart</button>
                  <button data-testid={`wishlist-remove-${p.slug}`} onClick={() => remove(p.slug)} className="font-mono-accent text-[10px] uppercase tracking-widest border border-white/10 px-3 py-2 hover:bg-[#EF4444] hover:border-[#EF4444] transition-colors">Remove</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Orders ---------------- */
export function OrdersSection() {
  const [orders, setOrders] = useState(null);
  const [reordering, setReordering] = useState(null);
  const { addItem, setOpen } = useCart();

  useEffect(() => { api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([])); }, []);

  const reorder = async (o) => {
    const lines = o.line_items || [];
    if (!lines.length) return toast.error("This order can't be reordered.");
    setReordering(o.session_id);
    try {
      const res = await Promise.all(lines.map((l) => api.get(`/products/${l.slug}`).then((r) => ({ p: r.data, q: l.quantity })).catch(() => null)));
      let added = 0;
      res.forEach((x) => { if (x && x.p) { addItem(x.p, x.q); added++; } });
      if (added) { toast.success("Items added to cart."); setOpen(true); } else toast.error("Products unavailable.");
    } finally { setReordering(null); }
  };

  const invoice = (id) => { window.open(`${API}/account/orders/${id}/invoice`, "_blank"); };

  return (
    <div data-testid="account-orders">
      <Head eyebrow="Order History" title="Your Orders" />
      {orders === null ? <Loading /> : orders.length === 0 ? (
        <div className="border border-white/10 rounded-2xl p-12 text-center font-mono-accent text-sm text-[#7a7a7a] uppercase tracking-widest">No orders yet</div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.session_id} data-testid={`order-${o.session_id}`} className="border border-white/10 rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold uppercase tracking-tight">{(o.items || []).join(", ")}</p>
                  <p className="font-mono-accent text-[11px] text-[#7a7a7a] mt-1">{new Date(o.created_at).toLocaleDateString()} · {String(o.method || "card").toUpperCase()} · {o.session_id.slice(0, 18)}…</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-accent text-[10px] uppercase tracking-widest bg-[#4ADE80] text-[#F5F5F5] px-2 py-1 rounded-full">Paid</span>
                  <span className="font-display font-black text-xl">{fmtPrice(o.amount)}</span>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid={`reorder-${o.session_id}`} onClick={() => reorder(o)} disabled={reordering === o.session_id} className="inline-flex items-center gap-2 border border-white/10 px-4 py-2 font-mono-accent text-[11px] uppercase tracking-widest hover:bg-[#6FE998] hover:text-[#F5F5F5] hover:border-[#4ADE80] transition-colors disabled:opacity-50">{reordering === o.session_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Reorder</button>
                <button data-testid={`invoice-${o.session_id}`} onClick={() => invoice(o.session_id)} className="inline-flex items-center gap-2 border border-white/10 px-4 py-2 font-mono-accent text-[11px] uppercase tracking-widest hover:bg-[#0F0F10] hover:text-[#F5F5F5] transition-colors"><Download className="h-3.5 w-3.5" /> Invoice</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Security ---------------- */
export function SecuritySection({ user }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const isSocial = (user.auth_providers || []).includes("google") && !(user.auth_providers || []).includes("password");

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("New passwords don't match.");
    setSaving(true);
    try {
      await api.put("/auth/password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update password."); } finally { setSaving(false); }
  };

  return (
    <div data-testid="account-security">
      <Head eyebrow="Security" title="Password" />
      {isSocial ? (
        <div className="border border-white/10 rounded-2xl p-8 max-w-lg flex items-start gap-4">
          <ShieldCheck className="h-6 w-6 text-[#4ADE80] shrink-0" />
          <p className="text-[#E5E5E5] leading-relaxed">Your account signs in with Google. Password management is handled by your Google account.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 max-w-lg">
          <input data-testid="current-password" required type="password" placeholder="Current password" className={inputCls} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
          <input data-testid="new-password" required type="password" placeholder="New password (min 6 chars)" className={inputCls} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
          <input data-testid="confirm-password" required type="password" placeholder="Confirm new password" className={inputCls} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
          <button data-testid="change-password-btn" type="submit" disabled={saving} className={btnPrimary}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Update Password</button>
        </form>
      )}
      <div className="mt-8 border border-white/10 rounded-2xl p-6 max-w-lg">
        <p className="font-display font-bold uppercase tracking-tight flex items-center gap-2"><CircleCheck className="h-4 w-4 text-[#4ADE80]" /> Session Security</p>
        <p className="mt-2 text-[#A8A8A8] text-sm leading-relaxed">You&apos;re signed in with a secure, HTTP-only session. Access tokens expire automatically and refresh in the background.</p>
      </div>
    </div>
  );
}

/* ---------------- Privacy ---------------- */
export function PrivacySection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "enda-g1-account-data.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export downloaded.");
    } catch { toast.error("Could not export data."); }
  };

  const del = async () => {
    setDeleting(true);
    try { await api.delete("/account"); await logout(); toast.success("Account deleted."); navigate("/", { replace: true }); }
    catch { toast.error("Could not delete account."); setDeleting(false); }
  };

  return (
    <div data-testid="account-privacy">
      <Head eyebrow="Privacy" title="Your Data" />
      <div className="space-y-4 max-w-lg">
        <div className="border border-white/10 rounded-2xl p-6">
          <p className="font-display font-bold uppercase tracking-tight">Export Your Data</p>
          <p className="mt-2 text-[#A8A8A8] text-sm leading-relaxed">Download a copy of your profile, addresses, wishlist, and order history (GDPR / PIPEDA).</p>
          <button data-testid="export-data-btn" onClick={exportData} className={btnGhost + " mt-4 inline-flex items-center gap-2"}><Download className="h-4 w-4" /> Export (JSON)</button>
        </div>
        <div className="border border-[#EF4444]/30 rounded-2xl p-6">
          <p className="font-display font-bold uppercase tracking-tight flex items-center gap-2 text-[#EF4444]"><AlertTriangle className="h-4 w-4" /> Delete Account</p>
          <p className="mt-2 text-[#A8A8A8] text-sm leading-relaxed">Permanently remove your account and personal data. Order records are anonymized for compliance. This cannot be undone.</p>
          <button data-testid="delete-account-btn" onClick={() => setConfirm(true)} className="mt-4 inline-flex items-center gap-2 border border-[#EF4444]/50 text-[#EF4444] font-mono-accent text-xs uppercase tracking-widest px-5 py-3 hover:bg-[#EF4444] hover:text-white transition-colors"><Trash2 className="h-4 w-4" /> Delete My Account</button>
        </div>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="bg-[#171717] border border-white/10 text-[#F5F5F5] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-2xl uppercase tracking-tighter">Delete account?</DialogTitle><DialogDescription className="font-mono-accent text-xs text-[#7a7a7a]">This permanently removes your profile, addresses, and wishlist.</DialogDescription></DialogHeader>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setConfirm(false)} className={btnGhost + " flex-1"}><X className="h-4 w-4 inline mr-1" /> Cancel</button>
            <button data-testid="confirm-delete-btn" onClick={del} disabled={deleting} className="flex-1 bg-[#EF4444] text-white font-display font-bold uppercase tracking-tight px-6 py-3 hover:bg-[#dc2626] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">{deleting && <Loader2 className="h-4 w-4 animate-spin" />} Delete</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------
// REWARDS
// ------------------------------------------------------------
export function RewardsSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/account/rewards").then((r) => setData(r.data)).catch(() => setData({ balance: 0, value_usd: 0, ledger: [], redeem_rate: "10 pts = $1" })); }, []);
  if (!data) return <Loading />;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Head eyebrow="Loyalty" title="Rewards" />
      <div data-testid="rewards-balance-card" className="border border-white/10 bg-gradient-to-br from-[#0F0F10] via-[#171717] to-[#0F0F10] rounded-2xl p-8 mb-6 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#4ADE80]/10 blur-[80px]" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#4ADE80]">Points Balance</div>
            <div className="mt-3 font-display text-5xl md:text-6xl text-[#F5F5F5]">{data.balance}</div>
            <div className="mt-2 text-sm text-[#A8A8A8]">≈ <span className="text-[#F5F5F5]">${data.value_usd.toFixed(2)}</span> in store credit</div>
            <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-[#7a7a7a]">Earn 1 pt per $1 · Redeem: {data.redeem_rate}</div>
          </div>
          <Gift className="h-10 w-10 text-[#4ADE80]" strokeWidth={1.4} />
        </div>
      </div>
      <div className="border border-white/10 bg-[#1F1F1F] rounded-2xl p-6">
        <h3 className="font-display text-lg mb-4 text-[#F5F5F5]">Recent activity</h3>
        {data.ledger.length === 0 ? (
          <p className="text-sm text-[#A8A8A8]">No points activity yet. Complete an order to start earning.</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {data.ledger.map((row) => (
              <li key={row.id} data-testid={`rewards-ledger-${row.id}`} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-[#F5F5F5] capitalize">{row.kind === "earn" ? "Earned" : "Redeemed"}</div>
                  <div className="text-xs text-[#A8A8A8]">Order {String(row.order_id || "").slice(0, 14)} · {String(row.created_at).slice(0, 10)}</div>
                </div>
                <div className={`font-mono ${row.kind === "earn" ? "text-[#4ADE80]" : "text-red-400"}`}>{row.kind === "earn" ? "+" : "-"}{row.points} pts</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
}

// ------------------------------------------------------------
// SUPPORT (Tickets)
// ------------------------------------------------------------
const STATUS_LABEL = {
  open: { text: "Open", cls: "bg-[#4ADE80]/20 text-[#4ADE80]" },
  awaiting_customer: { text: "Awaiting You", cls: "bg-amber-500/20 text-amber-300" },
  resolved: { text: "Resolved", cls: "bg-blue-500/20 text-blue-300" },
  closed: { text: "Closed", cls: "bg-white/10 text-[#A8A8A8]" },
};

function TicketThread({ ticket, onBack, onReply }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const canReply = ticket.status !== "closed";
  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/support/tickets/${ticket.ticket_number}/reply`, { message });
      onReply(data);
      setMessage("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };
  const s = STATUS_LABEL[ticket.status] || STATUS_LABEL.open;
  return (
    <div>
      <button onClick={onBack} className="text-[11px] uppercase tracking-[0.18em] text-[#A8A8A8] hover:text-[#F5F5F5] mb-6" data-testid="ticket-back">← Back to tickets</button>
      <div className="border border-white/10 bg-[#1F1F1F] rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">{ticket.ticket_number}</div>
            <h3 className="font-display text-2xl mt-1 text-[#F5F5F5]">{ticket.subject}</h3>
            <div className="text-xs text-[#A8A8A8] mt-1 capitalize">{ticket.category} · {String(ticket.created_at).slice(0, 10)}</div>
          </div>
          <span className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1 rounded-full ${s.cls}`}>{s.text}</span>
        </div>
        <ul className="mt-6 space-y-4">
          {(ticket.messages || []).map((m) => (
            <li key={m.id} className={`flex ${m.from === "admin" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] p-4 rounded-2xl ${m.from === "admin" ? "bg-[#0F0F10] border border-[#4ADE80]/30" : "bg-[#4ADE80]/10 border border-[#4ADE80]/20"}`}>
                <div className="text-[10px] uppercase tracking-[0.16em] mb-1.5" style={{ color: m.from === "admin" ? "#4ADE80" : "#A8A8A8" }}>
                  {m.from === "admin" ? "Support" : "You"} · {String(m.created_at).slice(11, 16)}
                </div>
                <div className="text-sm text-[#F5F5F5] whitespace-pre-wrap">{m.body}</div>
              </div>
            </li>
          ))}
        </ul>
        {canReply && (
          <div className="mt-6 pt-4 border-t border-white/10">
            <textarea data-testid="ticket-reply-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Type your reply…" rows={3} className={inputCls} />
            <button data-testid="ticket-reply-btn" onClick={send} disabled={sending || !message.trim()} className={btnPrimary + " mt-3"}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send reply
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function NewTicketForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ subject: "", category: "other", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success(`Ticket ${data.ticket_number} created`);
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create ticket"); }
    finally { setSending(false); }
  };
  return (
    <form onSubmit={submit} className="border border-white/10 bg-[#1F1F1F] rounded-2xl p-6 space-y-4">
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8] block mb-2">Subject</label>
        <input data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required className={inputCls} placeholder="Brief summary of your issue" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8] block mb-2">Category</label>
        <select data-testid="ticket-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
          <option value="order">Order</option>
          <option value="product">Product</option>
          <option value="shipping">Shipping</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8] block mb-2">Message</label>
        <textarea data-testid="ticket-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={5} className={inputCls} placeholder="Describe your issue in detail…" />
      </div>
      <div className="flex gap-3">
        <button data-testid="ticket-submit" type="submit" disabled={sending} className={btnPrimary}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Create Ticket
        </button>
        <button type="button" onClick={onCancel} className={btnGhost}>Cancel</button>
      </div>
    </form>
  );
}

export function SupportSection() {
  const [tickets, setTickets] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);
  if (tickets === null) return <Loading />;

  if (activeTicket) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        <TicketThread ticket={activeTicket} onBack={() => { setActiveTicket(null); load(); }} onReply={(t) => setActiveTicket(t)} />
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Head eyebrow="Help" title="Support Tickets">
        {!creating && <button data-testid="new-ticket-btn" onClick={() => setCreating(true)} className={btnPrimary}><Plus className="h-4 w-4" /> New Ticket</button>}
      </Head>
      {creating ? (
        <NewTicketForm onCreated={(t) => { setCreating(false); setActiveTicket(t); load(); }} onCancel={() => setCreating(false)} />
      ) : tickets.length === 0 ? (
        <div className="border border-white/10 bg-[#1F1F1F] rounded-2xl p-10 text-center">
          <LifeBuoy className="h-10 w-10 mx-auto text-[#4ADE80] mb-3" strokeWidth={1.4} />
          <p className="text-[#F5F5F5] font-display text-lg">No tickets yet</p>
          <p className="text-sm text-[#A8A8A8] mt-1">Need help? Open a ticket and our team will respond.</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-[#1F1F1F] rounded-2xl divide-y divide-white/5 overflow-hidden">
          {tickets.map((t) => {
            const s = STATUS_LABEL[t.status] || STATUS_LABEL.open;
            const last = (t.messages || []).slice(-1)[0];
            return (
              <button key={t.ticket_number} data-testid={`ticket-row-${t.ticket_number}`} onClick={() => setActiveTicket(t)} className="w-full text-left px-6 py-4 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8A8A8]">{t.ticket_number} · {t.category}</div>
                    <div className="text-[#F5F5F5] mt-0.5 truncate">{t.subject}</div>
                    {last && <div className="text-xs text-[#A8A8A8] mt-1 line-clamp-1"><MessageCircle className="inline h-3 w-3 mr-1" />{last.from === "admin" ? "Support" : "You"}: {last.body}</div>}
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1 rounded-full shrink-0 ${s.cls}`}>{s.text}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

